import { Action, Reducer } from 'redux';
import { StorageBrowserState, StorageBrowserUnloadedState } from '../states/StorageBrowserState';
import * as Actions from '../actions';

type KnownAction = Actions.StorageBrowser.StorageBrowserKnownAction;

export const StorageBrowserReducer: Reducer<StorageBrowserState> = (
  state: StorageBrowserState | undefined,
  incomingAction: Action
): StorageBrowserState => {
  if (state === undefined) {
    return StorageBrowserUnloadedState;
  }

  const action = incomingAction as KnownAction;

  switch (action.type) {
    case 'STORAGEBROWSER_BROWSE_REQUEST_ACTION':
      if (action.append) {
        if (action.path !== state.currentPath) return state;
        return { ...state, isLoadingMore: true, error: undefined };
      }
      // New folder (or refresh): start from scratch so stale rows are never shown under another path.
      return {
        ...state,
        currentPath: action.path,
        breadcrumbs: action.path === state.currentPath ? state.breadcrumbs : [],
        folders: [],
        files: [],
        continuationToken: null,
        currentFolder: action.path === state.currentPath ? state.currentFolder : null,
        isLoading: true,
        isLoadingMore: false,
        isLoaded: false,
        error: undefined,
        fileError: undefined,
      };
    case 'STORAGEBROWSER_BROWSE_RECEIVE_ACTION':
      // Late answer of a folder the user already left: ignore it.
      if (action.path !== state.currentPath) return state;
      return {
        ...state,
        breadcrumbs: action.result.breadcrumbs,
        folders: action.append ? [...state.folders, ...action.result.folders] : action.result.folders,
        files: action.append ? [...state.files, ...action.result.files] : action.result.files,
        continuationToken: action.result.continuationToken,
        currentFolder: action.append ? state.currentFolder : action.result.currentFolder ?? null,
        isLoading: false,
        isLoadingMore: false,
        isLoaded: true,
        error: undefined,
      };
    case 'STORAGEBROWSER_BROWSE_FAIL_ACTION':
      if (action.path !== state.currentPath) return state;
      return { ...state, isLoading: false, isLoadingMore: false, isLoaded: true, error: action.error };
    case 'STORAGEBROWSER_READSAS_REQUEST_ACTION':
      return { ...state, pendingFile: { path: action.path, action: action.action }, fileError: undefined };
    case 'STORAGEBROWSER_READSAS_SUCCESS_ACTION':
      return { ...state, pendingFile: undefined };
    case 'STORAGEBROWSER_READSAS_FAIL_ACTION':
      return {
        ...state,
        pendingFile: undefined,
        fileError: { path: action.path, action: action.action, error: action.error },
      };
    case 'STORAGEBROWSER_ENTITYNAME_REQUEST_ACTION':
      return { ...state, entityNames: { ...(state.entityNames ?? {}), [action.key]: { status: 'loading' } } };
    case 'STORAGEBROWSER_ENTITYNAME_RECEIVE_ACTION':
      return {
        ...state,
        entityNames: { ...(state.entityNames ?? {}), [action.key]: { status: 'loaded', name: action.name } },
      };
    case 'STORAGEBROWSER_ENTITYNAME_FAIL_ACTION':
      return { ...state, entityNames: { ...(state.entityNames ?? {}), [action.key]: { status: 'failed' } } };
    case 'STORAGEBROWSER_CLEAR_FILE_ERROR_ACTION':
      return { ...state, fileError: undefined };
    case 'STORAGEBROWSER_CREATEFOLDER_SUCCESS_ACTION':
      if (action.parentPath !== state.currentPath || state.folders.some((folder) => folder.path === action.folder.path)) {
        return state;
      }
      return {
        ...state,
        folders: [...state.folders, action.folder].sort((a, b) => a.name.localeCompare(b.name)),
      };
    case 'STORAGEBROWSER_RENAMEFOLDER_SUCCESS_ACTION':
      return {
        ...state,
        folders: state.folders.map((folder) =>
          folder.path === action.result.oldPath ? { ...folder, name: action.newName, path: action.result.newPath } : folder
        ),
      };
    case 'STORAGEBROWSER_DELETEFOLDER_SUCCESS_ACTION':
      return { ...state, folders: state.folders.filter((folder) => folder.path !== action.path) };
    case 'STORAGEBROWSER_DELETEFILE_SUCCESS_ACTION':
      return { ...state, files: state.files.filter((file) => file.path !== action.path) };
    default:
      return state;
  }
};
